import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
            <p className="text-gray-600">Last Updated: August 31, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="mb-6">
              Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully before using the RealEstart.io website (the "Service") operated by Signal Cloud LLC ("us", "we", or "our").
            </p>
            
            <p className="mb-6">
              Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who wish to access or use the Service.
            </p>
            
            <p className="mb-8">
              By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you do not have permission to access the Service.
            </p>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Accounts</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you create an account with us, you guarantee that the information you provide is accurate, complete, and current at all times. Inaccurate, incomplete, or obsolete information may result in the immediate termination of your account on the Service.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Billing and Subscriptions</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Service is billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis (e.g., monthly or annually), depending on the subscription plan you select.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  At the end of each period, your Subscription will automatically renew under the exact same conditions unless you cancel it or Signal Cloud LLC cancels it. You may cancel your Subscription renewal either through your online account management page or by contacting our customer support team. Upon cancellation, your subscription will not be renewed, and you will not be charged for future billing cycles. However, you will retain access to the Service for the remainder of your current paid subscription period.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  A valid payment method, such as a credit card or PayPal account, is required to process the payment for your Subscription. You shall provide Signal Cloud LLC with accurate and complete billing information, including full name, address, state, zip code, telephone number, and a valid payment method information. By submitting such payment information, you automatically authorize Signal Cloud LLC to charge all Subscription fees incurred through your account to any such payment instruments.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Refunds</h2>
                <p className="text-gray-700 leading-relaxed">
                  All Subscription fees are non-refundable. We will not provide refunds or credits for any partial subscription periods. Notwithstanding the foregoing, we reserve the right, at our sole and absolute discretion, to offer a refund, credit, or other form of compensation in unusual or exceptional circumstances. The amount and nature of any such refund will be determined by us and shall not be construed as an obligation to provide similar compensation in the future.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Intellectual Property</h2>
                <p className="text-gray-700 leading-relaxed">
                  The Service and its original content (excluding content provided by users), features, and functionality are and will remain the exclusive property of Signal Cloud LLC and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Signal Cloud LLC.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Prohibited Uses</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You may use the Service only for lawful purposes and in accordance with the Terms. You agree not to use the Service:
                </p>
                <ul className="list-disc list-inside ml-6 mb-4 space-y-2 text-gray-700">
                  <li>In any way that violates any applicable national or international law or regulation.</li>
                  <li>To exploit, harm, or attempt to exploit or harm minors in any way.</li>
                  <li>To transmit, or procure the sending of, any advertising or promotional material, including "junk mail," "chain letter," "spam," or any other similar solicitation.</li>
                  <li>To impersonate or attempt to impersonate Signal Cloud LLC, a Signal Cloud LLC employee, another user, or any other person or entity.</li>
                  <li>To engage in any other conduct that restricts or inhibits anyone's use and enjoyment of the Service, or which, as determined by us, may harm Signal Cloud LLC or users of the Service or expose them to liability.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Termination</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Governing Law</h2>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed and construed in accordance with the laws of Georgia, United States, without regard to its conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Changes to Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions about these Terms, please contact us.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/" 
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
